import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Switch,
  Text,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  Tooltip,
} from "@chakra-ui/react";
import { useRef, useState, useEffect } from "react";
import {
  Plus,
  Pencil,
  Save,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Stethoscope,
  Gift,
  FileText,
  Briefcase,
  CheckSquare,
  Layers,
  Hash,
  AlertCircle,
  Clock,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { useMissionsAdmin } from "./useMissionsAdmin";
import { MissionFormModal } from "./components/MissionFormModal";
import type { FieldChangeset } from "./components/useFormBuilder";
import { useFormBuilder } from "./components/useFormBuilder";
import { FormBuilder } from "./components/FormBuilder";
import type { MissionDefinition, CreateMissionPayload, UpdateMissionPayload } from "./types";
import { OnflyLoading } from "../../../tools/OnflyLoading";
import { ErrorState } from "../../../components/ErrorState";
import { AdminTabSwitch } from "../../../components/AdminTabSwitch";
import TicketMappingsPage from "../ticket-mappings/TicketMappingsPage";

// ─── helpers ─────────────────────────────────────────────────────────────────

const MISSION_ICONS = [ClipboardList, Stethoscope, Gift, FileText, Briefcase, CheckSquare] as const;
const getMissionIcon = (order: number) => MISSION_ICONS[(order - 1) % MISSION_ICONS.length];

interface StatPillProps { icon: React.ElementType; label: string }
const StatPill = ({ icon, label }: StatPillProps) => {
  const bg = useColorModeValue("white", "#0f2040");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const textColor = useColorModeValue("#434347", "#e8edf2");
  return (
    <Flex align="center" gap={1.5} px={3} py={1.5} borderRadius="full" borderWidth="1px" borderColor={border} bg={bg} boxShadow="xs">
      <Icon as={icon} boxSize={3.5} color="#009efb" />
      <Text fontSize="sm" fontWeight={500} color={textColor}>{label}</Text>
    </Flex>
  );
};

// ─── MissionCard (somente o card — sem a timeline) ───────────────────────────

const MissionCard = ({
  mission,
  totalMissions,
  isDragging,
  onEdit,
  onToggleActive,
  onDeleteMission,
  bulkUpdateFields,
  otherMissions,
}: {
  mission: MissionDefinition;
  totalMissions: number;
  isDragging: boolean;
  onEdit: (m: MissionDefinition) => void;
  onToggleActive: (m: MissionDefinition) => void;
  onDeleteMission: (m: MissionDefinition) => void;
  bulkUpdateFields: (missionId: number, changeset: FieldChangeset) => Promise<void>;
  otherMissions: MissionDefinition[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    fields,
    reorderFields,
    addField: addFieldToState,
    updateField: updateFieldInState,
    deleteField: deleteFieldFromState,
    getChangeset,
    hasChanges,
    resetFields,
  } = useFormBuilder(mission.fields);

  const handleSaveFields = async () => {
    setSaving(true);
    try {
      const updatedMission = await bulkUpdateFields(mission.id, getChangeset());
      // Reinicializa o estado com os dados frescos da API (uids numéricos reais)
      if (updatedMission?.fields) {
        resetFields(updatedMission.fields);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const cardBg = useColorModeValue("white", "#0f2040");
  const cardBorder = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const iconBg = useColorModeValue("rgba(0,158,251,0.10)", "rgba(0,158,251,0.15)");
  const panelBg = useColorModeValue("#f3f3f3", "#0a1628");
  const panelBorder = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.06)");
  const titleColor = useColorModeValue("#434347", "#e8edf2");
  const descColor = useColorModeValue("#696a71", "#a0aec0");
  const subtitleColor = useColorModeValue("#84858c", "#718096");
  const panelTitleColor = useColorModeValue("#434347", "#e8edf2");
  const panelSubColor = useColorModeValue("#696a71", "#a0aec0");
  const toggleLabelColor = useColorModeValue("#84858c", "#a0aec0");
  const MissionIcon = getMissionIcon(mission.order);

  return (
    <Box
      flex={1}
      bg={cardBg}
      borderRadius="12px"
      borderWidth="1px"
      borderColor={isDragging ? "#009efb" : cardBorder}
      overflow="hidden"
      boxShadow={isDragging ? "xl" : "0px 4px 8px 0px rgba(14, 61, 93, 0.08)"}
      _hover={{ boxShadow: isDragging ? "xl" : "0 2px 8px rgba(0, 158, 251, 0.12)" }}
      transition="box-shadow 0.15s, border-color 0.15s"
    >
      {/* Main row */}
      <Flex align="center" gap={{ base: 2, md: 4 }} px={{ base: 3, md: 5 }} py={{ base: 3, md: 4 }}>
        <Flex w="40px" h="40px" borderRadius="10px" bg={iconBg} align="center" justify="center" flexShrink={0}>
          <Icon as={MissionIcon} boxSize={5} color="#009efb" />
        </Flex>

        <Box flex={1} minW={0}>
          <Text fontWeight={600} fontSize="md" color={titleColor} noOfLines={1}>{mission.title}</Text>
          {mission.description && (
            <Text fontSize="sm" color={descColor} noOfLines={1} mt={0.5}>{mission.description}</Text>
          )}
          <Text fontSize="xs" color={subtitleColor} mt={1}>
            Etapa {mission.order} de {totalMissions}
          </Text>

          {/* Badges + switch — mobile only */}
          <Flex display={{ base: "flex", md: "none" }} mt={2} gap={1.5} wrap="wrap" align="center">
            {mission.is_required ? (
              <Badge borderRadius="50px" bg="rgba(237,119,19,0.12)" color="#ed7713" fontSize="11px" fontWeight={600} px={2.5} py={0.5}>Obrigatória</Badge>
            ) : (
              <Badge borderRadius="50px" bg="rgba(76,175,80,0.2)" color="#4caf50" border="1px solid rgba(67,67,71,0.3)" fontSize="11px" px={2.5} py={0.5}>Opcional</Badge>
            )}
            {mission.requires_internal_wait && (
              <Badge borderRadius="50px" bg="rgba(237,119,19,0.12)" color="#ed7713" fontSize="11px" fontWeight={600} px={2.5} py={0.5} display="flex" alignItems="center" gap={1}>
                <Icon as={Clock} boxSize={3} />Aguardo RH
              </Badge>
            )}
            {mission.is_pj && (
              <Badge borderRadius="50px" bg="rgba(139,92,246,0.12)" color="#7c3aed" fontSize="11px" fontWeight={600} px={2.5} py={0.5}>PJ</Badge>
            )}
            <Badge borderRadius="50px" bg="rgba(0,158,251,0.12)" color="#009efb" fontSize="11px" px={2.5} py={0.5}>
              {mission.fields.length} Campos
            </Badge>
            <Flex align="center" gap={1.5}>
              <Switch isChecked={mission.is_active} size="sm" colorScheme="blue" onChange={() => onToggleActive(mission)} />
              <Text fontSize="xs" color={toggleLabelColor}>Ativa</Text>
            </Flex>
          </Flex>
        </Box>

        {/* Desktop: badges */}
        <HStack display={{ base: "none", md: "flex" }} spacing={2} flexShrink={0}>
          {mission.is_required ? (
            <Badge borderRadius="50px" bg="rgba(237,119,19,0.12)" color="#ed7713" fontSize="11px" fontWeight={600} px={2.5} py={0.5} letterSpacing="0.5px">
              Obrigatória
            </Badge>
          ) : (
            <Badge borderRadius="50px" bg="rgba(76, 175, 80, 0.2)" color="#4caf50" border="1px solid rgba(67,67,71,0.3)" fontSize="11px" px={2.5} py={0.5} letterSpacing="0.5px">
              Opcional
            </Badge>
          )}
          {mission.requires_internal_wait && (
            <Tooltip label="Esta etapa aguarda ação interna do RH" openDelay={400}>
              <Badge borderRadius="50px" bg="rgba(237,119,19,0.12)" color="#ed7713" fontSize="11px" fontWeight={600} px={2.5} py={0.5} display="flex" alignItems="center" gap={1}>
                <Icon as={Clock} boxSize={3} />
                Aguardo RH
              </Badge>
            </Tooltip>
          )}
          {mission.is_pj && (
            <Badge borderRadius="50px" bg="rgba(139,92,246,0.12)" color="#7c3aed" fontSize="11px" fontWeight={600} px={2.5} py={0.5} letterSpacing="0.5px">
              PJ
            </Badge>
          )}
          <Badge borderRadius="50px" bg="rgba(0,158,251,0.12)" color="#009efb" fontSize="11px" px={2.5} py={0.5}>
            {mission.fields.length} Campos
          </Badge>
        </HStack>

        {/* Desktop: switch + ativa */}
        <Flex display={{ base: "none", md: "flex" }} align="center" gap={1.5} pl={3} borderLeftWidth="1px" borderColor={cardBorder} flexShrink={0}>
          <Switch
            isChecked={mission.is_active}
            size="sm"
            colorScheme="blue"
            onChange={() => onToggleActive(mission)}
          />
          <Text fontSize="xs" color={toggleLabelColor}>Ativa</Text>
        </Flex>

        <HStack spacing={0} flexShrink={0}>
          <Tooltip label="Configurações" openDelay={400}>
            <IconButton
              aria-label="Editar missão"
              icon={<Pencil size={14} />}
              size="sm"
              variant="ghost"
              onClick={() => onEdit(mission)}
            />
          </Tooltip>
          <IconButton
            aria-label={isExpanded ? "Recolher" : "Ver campos"}
            icon={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            size="sm"
            variant="ghost"
            color={subtitleColor}
            onClick={() => setIsExpanded((v) => !v)}
          />
        </HStack>
      </Flex>

      {/* Fields panel */}
      <Collapse in={isExpanded} animateOpacity>
        <Box borderTopWidth="1px" borderColor={panelBorder} bg={panelBg} px={5} py={4}>
          <Flex justify="space-between" align="center" mb={3}>
            <Box>
              <Text fontWeight={600} fontSize="sm" color={panelTitleColor}>Campos</Text>
              <Text fontSize="xs" color={panelSubColor}>
                Arraste para reordenar · clique no lápis para editar
              </Text>
            </Box>
            {hasChanges && (
              <Button size="sm" bg="#009efb" color="white" _hover={{ bg: "#007bd4" }} borderRadius="50px" leftIcon={<Save size={13} />} onClick={handleSaveFields} isLoading={saving}>
                Salvar
              </Button>
            )}
          </Flex>
          <FormBuilder
            fields={fields}
            onReorder={reorderFields}
            onAdd={addFieldToState}
            onUpdate={updateFieldInState}
            onDelete={deleteFieldFromState}
            allMissions={otherMissions}
          />
        </Box>
      </Collapse>
    </Box>
  );
};


export default function MissionsAdminPage() {
  const {
    missions,
    loading,
    fetchError,
    fetchMissions,
    createMission,
    updateMission,
    deleteMission,
    bulkUpdateFields,
  } = useMissionsAdmin();

  const [missionModal, setMissionModal] = useState<{
    isOpen: boolean;
    mission: MissionDefinition | null;
  }>({ isOpen: false, mission: null });
  const [deleteTarget, setDeleteTarget] = useState<MissionDefinition | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteMission(deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  // Ordered IDs for optimistic drag reorder
  const [orderedIds, setOrderedIds] = useState<number[]>([]);

  useEffect(() => {
    setOrderedIds([...missions].sort((a, b) => a.order - b.order).map((m) => m.id));
  }, [missions]);

  const sortedMissions = orderedIds
    .map((id) => missions.find((m) => m.id === id))
    .filter((m): m is MissionDefinition => m !== undefined);

  const totalFields = missions.reduce((sum, m) => sum + m.fields.length, 0);
  const requiredCount = missions.filter((m) => m.is_required).length;

  const pageBg = useColorModeValue("#f3f3f3", "#0a1628");
  const addRowBorderColor = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");
  const addRowHoverBg = useColorModeValue("rgba(0,158,251,0.06)", "rgba(0,158,251,0.08)");
  const addIconColor = useColorModeValue("#84858c", "#718096");
  const addLabelColor = useColorModeValue("#696a71", "#a0aec0");
  const addSubColor = useColorModeValue("#84858c", "#718096");

  const handleMissionDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const newIds = Array.from(orderedIds);
    const [moved] = newIds.splice(result.source.index, 1);
    newIds.splice(result.destination.index, 0, moved);

    setOrderedIds(newIds);

    await Promise.all(
      newIds
        .map((id, i) => {
          const mission = missions.find((m) => m.id === id);
          const newOrder = i + 1;
          if (mission && mission.order !== newOrder) {
            return updateMission(id, { order: newOrder });
          }
        })
        .filter((p): p is Promise<void> => p !== undefined)
    );
  };

  const handleMissionSubmit = async (
    payload: CreateMissionPayload | UpdateMissionPayload,
    fieldChanges: FieldChangeset
  ) => {
    if (missionModal.mission) {
      await updateMission(missionModal.mission.id, payload as UpdateMissionPayload);
      await bulkUpdateFields(missionModal.mission.id, fieldChanges);
    } else {
      const newMission = await createMission(payload as CreateMissionPayload);
      await bulkUpdateFields(newMission.id, { toAdd: fieldChanges.toAdd, toUpdate: [], toDelete: [] });
    }
    fetchMissions();
  };

  const handleToggleActive = async (mission: MissionDefinition) => {
    await updateMission(mission.id, { is_active: !mission.is_active });
  };

  const etapasContent = (
    <>
      {/* ── Stat pills ── */}
      {missions.length > 0 && (
        <Box mb={8}>
          <HStack spacing={3} wrap="wrap">
            <StatPill icon={Layers} label={`${missions.length} ${missions.length === 1 ? "etapa" : "etapas"}`} />
            <StatPill icon={AlertCircle} label={`${requiredCount} obrigatórias`} />
            <StatPill icon={Hash} label={`${totalFields} campos no total`} />
          </HStack>
        </Box>
      )}

      {/* ── Error ── */}
      {fetchError && (
        <ErrorState onRetry={fetchMissions} />
      )}

      {/* ── Content ── */}
      {loading ? (
        <Flex align="center" justify="center" py={20}>
          <OnflyLoading containerSize={80} />
        </Flex>
      ) : (
        <DragDropContext onDragEnd={handleMissionDragEnd}>
          <Droppable droppableId="missions-list">
            {(droppableProvided) => (
              <Box ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                {sortedMissions.map((mission, index) => {
                  const isLast = index === sortedMissions.length - 1;
                  return (
                    <Draggable key={mission.id} draggableId={String(mission.id)} index={index}>
                      {(provided, snapshot) => (
                        <Flex
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          align="stretch"
                          mb={isLast ? 0 : 4}
                        >
                          {/* ── Timeline column (drag handle) ── */}
                          <Flex direction="column" align="center" w="40px" flexShrink={0} mr={4}>
                            <Tooltip label="Arraste para reordenar" openDelay={600} placement="left">
                              <Flex
                                {...provided.dragHandleProps}
                                w="40px"
                                h="40px"
                                mt={5}
                                borderRadius="full"
                                bg={snapshot.isDragging ? "#007bd4" : "#009efb"}
                                color="white"
                                align="center"
                                justify="center"
                                fontWeight={700}
                                fontSize="sm"
                                flexShrink={0}
                                position="relative"
                                zIndex={1}
                                cursor="grab"
                                _active={{ cursor: "grabbing" }}
                                transition="background 0.15s, box-shadow 0.15s"
                                boxShadow={
                                  snapshot.isDragging
                                    ? "0 6px 24px rgba(0,158,251,0.45)"
                                    : `0 0 0 4px ${pageBg}`
                                }
                              >
                                {index + 1}
                              </Flex>
                            </Tooltip>
                            {!isLast && !snapshot.isDragging && (
                              <Box
                                flex={1}
                                w="2px"
                                bgImage="repeating-linear-gradient(to bottom, #009efb 0px, #009efb 4px, transparent 4px, transparent 8px)"
                                mt={1}
                              />
                            )}
                          </Flex>

                          {/* ── Card content ── */}
                          <MissionCard
                            mission={mission}
                            totalMissions={sortedMissions.length}
                            isDragging={snapshot.isDragging}
                            onEdit={(m) => setMissionModal({ isOpen: true, mission: m })}
                            onToggleActive={handleToggleActive}
                            onDeleteMission={setDeleteTarget}
                            bulkUpdateFields={bulkUpdateFields}
                            otherMissions={sortedMissions.filter(m => m.id !== mission.id)}
                          />
                        </Flex>
                      )}
                    </Draggable>
                  );
                })}

                {droppableProvided.placeholder}

                {/* ── Add mission row ── */}
                <Flex align="flex-start" mt={sortedMissions.length > 0 ? 4 : 0}>
                  <Flex direction="column" align="center" w="40px" flexShrink={0} mr={4}>
                    <Flex
                      w="40px" h="40px" mt={5}
                      borderRadius="full"
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderColor={addRowBorderColor}
                      align="center"
                      justify="center"
                      color={addIconColor}
                    >
                      <Plus size={16} />
                    </Flex>
                  </Flex>
                  <Box
                    flex={1}
                    mt={4}
                    p={4}
                    borderRadius="12px"
                    borderWidth="1.5px"
                    borderStyle="dashed"
                    borderColor={addRowBorderColor}
                    cursor="pointer"
                    transition="all 0.15s"
                    _hover={{ borderColor: "#009efb", bg: addRowHoverBg }}
                    onClick={() => setMissionModal({ isOpen: true, mission: null })}
                  >
                    <Text fontWeight={600} fontSize="sm" color={addLabelColor}>
                      Adicionar nova missão
                    </Text>
                    <Text fontSize="xs" color={addSubColor} mt={0.5}>
                      Inclua mais uma etapa no fluxo de admissão.
                    </Text>
                  </Box>
                </Flex>
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </>
  );

  return (
    <Box p={{ base: 2, md: 10 }}>
      <AdminTabSwitch
        tabs={[
          { label: "Gestão de Etapas", content: etapasContent },
          { label: "Mapeamento de Campos", content: <TicketMappingsPage standalone={false} /> },
        ]}
      />

      <MissionFormModal
        key={missionModal.mission?.id ?? "new"}
        isOpen={missionModal.isOpen}
        onClose={() => setMissionModal((prev) => ({ ...prev, isOpen: false }))}
        onSubmit={handleMissionSubmit}
        mission={missionModal.mission}
        totalMissions={missions.length}
      />

      <AlertDialog
        isOpen={!!deleteTarget}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteTarget(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="md" fontWeight={600}>Remover Missão</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Tem certeza que deseja remover{" "}
              <Text as="span" fontWeight={600}>{deleteTarget?.title}</Text>?{" "}
              Todos os campos e respostas associadas serão perdidos.
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button ref={cancelRef} variant="ghost" size="sm" onClick={() => setDeleteTarget(null)} isDisabled={isDeleting}>
                Cancelar
              </Button>
              <Button colorScheme="red" size="sm" onClick={handleConfirmDelete} isLoading={isDeleting} loadingText="Removendo...">
                Remover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
